// src/app/features/offers-policies/store/offers-policies.reducer.ts
//
// IMPORTANT — cache-aware Load handlers:
// Effects return EMPTY when the cache is valid (no HTTP call, no Success action).
// If the reducer set `loading: true` on every Load, the spinner would never turn
// off. Each Load handler therefore checks `isCacheValid()` before mutating state.

import { createReducer, on } from '@ngrx/store';

import {
  AsyncSlice,
  CacheEntry,
  OffersPoliciesState,
  TTL,
  emptySlice,
  initialOffersPoliciesState,
  isCacheValid,
} from './offers-policies.state';

import {
  // Policies
  PolicySearchActions,
  PolicyDetailActions,
  PolicySummaryActions,
  PolicyPaymentsActions,
  PolicyClaimsActions,
  // Installments
  InstallmentsByPolicyActions,
  // Invoices
  InvoiceSearchActions,
  InvoiceDetailActions,
  PolicyCacheActions,
} from './offers-policies.actions';

// ── Slice helpers ────────────────────────────────────────────────────────────

function setLoading<T>(slice: AsyncSlice<T>): AsyncSlice<T> {
  return { ...slice, loading: true, error: null, errorStatus: null };
}

function setSuccess<T>(slice: AsyncSlice<T>, data: T, ttlMs: number): AsyncSlice<T> {
  const cache: CacheEntry<T> = { data, loadedAt: Date.now(), ttlMs };
  return { data, loading: false, loaded: true, error: null, cache };
}

function setFailure<T>(slice: AsyncSlice<T>, error: string, status?: number): AsyncSlice<T> {
  return { ...slice, loading: false, error, errorStatus: status ?? null };
}

/** Copy of a keyed slice map without the given keys (same reference when nothing is removed). */
function withoutKeys<V>(map: Record<number, V>, keys: readonly number[]): Record<number, V> {
  if (!keys.some(k => k in map)) return map;
  const next = { ...map };
  for (const k of keys) delete next[k];
  return next;
}

// ── Reducer ──────────────────────────────────────────────────────────────────

export const offersPoliciesReducer = createReducer(
  initialOffersPoliciesState,

  // =========================================================================
  // ── POLICIES ──
  // =========================================================================

  // Policy search
  on(PolicySearchActions.search, (state): OffersPoliciesState => ({
    ...state,
    policies: { ...state.policies, search: setLoading(state.policies.search) },
  })),
  on(PolicySearchActions.success, (state, { data }): OffersPoliciesState => ({
    ...state,
    policies: { ...state.policies, search: setSuccess(state.policies.search, data, TTL.SEARCH) },
  })),
  on(PolicySearchActions.failure, (state, { error }): OffersPoliciesState => ({
    ...state,
    policies: { ...state.policies, search: setFailure(state.policies.search, error) },
  })),
  on(PolicySearchActions.clear, (state): OffersPoliciesState => ({
    ...state,
    policies: { ...state.policies, search: emptySlice() },
  })),

  // Policy detail (keyed)
  on(PolicyDetailActions.load, (state, { policyId, forceReload }): OffersPoliciesState => {
    const current = state.policies.details[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      policies: { ...state.policies, details: { ...state.policies.details, [policyId]: setLoading(current) } },
    };
  }),
  on(PolicyDetailActions.success, (state, { policyId, data }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      details: {
        ...state.policies.details,
        [policyId]: setSuccess(state.policies.details[policyId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(PolicyDetailActions.failure, (state, { policyId, error }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      details: {
        ...state.policies.details,
        [policyId]: setFailure(state.policies.details[policyId] ?? emptySlice(), error),
      },
    },
  })),

  // Policy summary
  on(PolicySummaryActions.load, (state, { policyId, forceReload }): OffersPoliciesState => {
    const current = state.policies.summaries[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      policies: { ...state.policies, summaries: { ...state.policies.summaries, [policyId]: setLoading(current) } },
    };
  }),
  on(PolicySummaryActions.success, (state, { policyId, data }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      summaries: {
        ...state.policies.summaries,
        [policyId]: setSuccess(state.policies.summaries[policyId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(PolicySummaryActions.failure, (state, { policyId, error }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      summaries: {
        ...state.policies.summaries,
        [policyId]: setFailure(state.policies.summaries[policyId] ?? emptySlice(), error),
      },
    },
  })),

  // Policy payments
  on(PolicyPaymentsActions.load, (state, { policyId, forceReload }): OffersPoliciesState => {
    const current = state.policies.payments[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      policies: { ...state.policies, payments: { ...state.policies.payments, [policyId]: setLoading(current) } },
    };
  }),
  on(PolicyPaymentsActions.success, (state, { policyId, data }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      payments: {
        ...state.policies.payments,
        [policyId]: setSuccess(state.policies.payments[policyId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(PolicyPaymentsActions.failure, (state, { policyId, error }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      payments: {
        ...state.policies.payments,
        [policyId]: setFailure(state.policies.payments[policyId] ?? emptySlice(), error),
      },
    },
  })),

  // Policy claims
  on(PolicyClaimsActions.load, (state, { policyId, forceReload }): OffersPoliciesState => {
    const current = state.policies.claims[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      policies: { ...state.policies, claims: { ...state.policies.claims, [policyId]: setLoading(current) } },
    };
  }),
  on(PolicyClaimsActions.success, (state, { policyId, data }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      claims: {
        ...state.policies.claims,
        [policyId]: setSuccess(state.policies.claims[policyId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(PolicyClaimsActions.failure, (state, { policyId, error }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      claims: {
        ...state.policies.claims,
        [policyId]: setFailure(state.policies.claims[policyId] ?? emptySlice(), error),
      },
    },
  })),

  // =========================================================================
  // ── INSTALLMENTS ──
  // =========================================================================

  on(InstallmentsByPolicyActions.load, (state, { policyId, forceReload }): OffersPoliciesState => {
    const current = state.installments.byPolicy[policyId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      installments: { ...state.installments, byPolicy: { ...state.installments.byPolicy, [policyId]: setLoading(current) } },
    };
  }),
  on(InstallmentsByPolicyActions.success, (state, { policyId, data }): OffersPoliciesState => ({
    ...state,
    installments: {
      ...state.installments,
      byPolicy: {
        ...state.installments.byPolicy,
        [policyId]: setSuccess(state.installments.byPolicy[policyId] ?? emptySlice(), data, TTL.PER_POLICY),
      },
    },
  })),
  on(InstallmentsByPolicyActions.failure, (state, { policyId, error }): OffersPoliciesState => ({
    ...state,
    installments: {
      ...state.installments,
      byPolicy: {
        ...state.installments.byPolicy,
        [policyId]: setFailure(state.installments.byPolicy[policyId] ?? emptySlice(), error),
      },
    },
  })),

  // =========================================================================
  // ── INVOICES ──
  // =========================================================================

  on(InvoiceSearchActions.search, (state, { request }): OffersPoliciesState => ({
    ...state,
    invoices: { ...state.invoices, search: setLoading(state.invoices.search), searchRequest: request },
  })),
  on(InvoiceSearchActions.success, (state, { data }): OffersPoliciesState => ({
    ...state,
    invoices: { ...state.invoices, search: setSuccess(state.invoices.search, data, TTL.SEARCH) },
  })),
  on(InvoiceSearchActions.failure, (state, { error }): OffersPoliciesState => ({
    ...state,
    invoices: { ...state.invoices, search: setFailure(state.invoices.search, error) },
  })),
  on(InvoiceSearchActions.clear, (state): OffersPoliciesState => ({
    ...state,
    invoices: { ...state.invoices, search: emptySlice(), searchRequest: null },
  })),

  on(InvoiceDetailActions.load, (state, { invoiceId, forceReload }): OffersPoliciesState => {
    const current = state.invoices.details[invoiceId] ?? emptySlice();
    if (!forceReload && isCacheValid(current.cache)) return state;
    return {
      ...state,
      invoices: { ...state.invoices, details: { ...state.invoices.details, [invoiceId]: setLoading(current) } },
    };
  }),
  on(InvoiceDetailActions.success, (state, { invoiceId, data }): OffersPoliciesState => ({
    ...state,
    invoices: {
      ...state.invoices,
      details: {
        ...state.invoices.details,
        [invoiceId]: setSuccess(state.invoices.details[invoiceId] ?? emptySlice(), data, TTL.PER_INVOICE),
      },
    },
  })),
  on(InvoiceDetailActions.failure, (state, { invoiceId, error, status }): OffersPoliciesState => ({
    ...state,
    invoices: {
      ...state.invoices,
      details: {
        ...state.invoices.details,
        [invoiceId]: setFailure(state.invoices.details[invoiceId] ?? emptySlice(), error, status),
      },
    },
  })),

  // Invoice create / cancel / remove-policy rewrote these policies' invoice link
  // and installment schedule — forget what is cached about them.
  on(PolicyCacheActions.invalidate, (state, { policyIds }): OffersPoliciesState => ({
    ...state,
    policies: {
      ...state.policies,
      details:    withoutKeys(state.policies.details, policyIds),
      summaries:  withoutKeys(state.policies.summaries, policyIds),
      payments:   withoutKeys(state.policies.payments, policyIds),
    },
    installments: {
      ...state.installments,
      byPolicy: withoutKeys(state.installments.byPolicy, policyIds),
    },
  })),
);
